import { useLocation } from 'react-router-dom';
import { useFeatureFlag, FeatureFlagKey } from './useFeatureFlags';
import { useAuth } from './useAuth';
import {
  hasCompletedOnboarding,
  isOnboardingSuppressed,
} from '../services/onboarding/onboardingStorage';
import { landedOnDeepLink } from '../services/onboarding/landingPage';

/**
 * Returns true when PSP should redirect the current user to /onboarding.
 * Conditions: onboarding flag is on, Auth0 user is known, that user hasn't
 * completed onboarding, they aren't already on `/onboarding`, and this visit
 * didn't begin on a deep link (the tour would lose it — see `landingPage.ts`).
 *
 * `sessionStorage['psp.suppressOnboarding'] === 'true'` short-circuits to
 * false so E2E suites can disable the redirect without changing LaunchDarkly.
 */
export function useShouldShowOnboarding(): boolean {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const userId = user?.sub;
  const onboardingEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_ONBOARDING_EXPERIENCE);

  if (isOnboardingSuppressed() || !userId) {
    return false;
  }
  // Read as soon as the user is known, whatever the flag says yet, so the landing page is the one
  // this visit began on rather than wherever the user is when the flag arrives.
  const deepLinkVisit = landedOnDeepLink();

  return (
    !!onboardingEnabled &&
    !hasCompletedOnboarding(userId) &&
    !pathname.startsWith('/onboarding') &&
    !deepLinkVisit
  );
}
